import React, { useState, useEffect } from 'react';
import { Card, Row, Col, ProgressBar, Badge, Button, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    users: { synced: 0, total: 0, percentage: 0 },
    categories: { synced: 0, total: 0, percentage: 0 },
    courses: { synced: 0, total: 0, percentage: 0 },
    enrollments: { synced: 0, total: 0, percentage: 0 }
  });
  const [systemStatus, setSystemStatus] = useState('loading');
  const [systemError, setSystemError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000); // Auto-refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const [userRes, categoryRes, courseRes, enrollmentRes] = await Promise.all([
        api.get('/users/stats'),
        api.get('/categories/stats'),
        api.get('/courses/stats'),
        api.get('/enrollments/stats')
      ]);

      const userData = userRes.data;
      const totalUsers = userData.teachers.total + userData.students.total;
      const syncedUsers = userData.teachers.synced + userData.students.synced;
      const userPercentage = totalUsers > 0 ? Math.round((syncedUsers / totalUsers) * 100) : 0;

      const categoryData = categoryRes.data;
      const totalCategories = Object.values(categoryData).reduce((sum, cat) => sum + cat.total, 0);
      const syncedCategories = Object.values(categoryData).reduce((sum, cat) => sum + cat.synced, 0);
      const categoryPercentage = totalCategories > 0 ? Math.round((syncedCategories / totalCategories) * 100) : 0;

      const courseData = courseRes.data;
      const courseStats = courseData.Subject || { total: 0, synced: 0, percentage: 0 };

      const enrollmentData = enrollmentRes.data;
      const enrollmentStats = enrollmentData.Enrollment || { total: 0, synced: 0, percentage: 0 };

      setStats({
        users: { synced: syncedUsers, total: totalUsers, percentage: userPercentage, details: userData },
        categories: { synced: syncedCategories, total: totalCategories, percentage: categoryPercentage, details: categoryData },
        courses: { synced: courseStats.synced, total: courseStats.total, percentage: courseStats.percentage },
        enrollments: { synced: enrollmentStats.synced, total: enrollmentStats.total, percentage: enrollmentStats.percentage }
      });

      setSystemStatus('active');
      setSystemError(null);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading stats:', error);
      setSystemStatus('error');
      setSystemError(error.response?.data?.error || error.message || 'Error de conexión');
    }
  };

  const getOverallProgress = () => {
    const totalItems = stats.users.total + stats.categories.total + stats.courses.total + stats.enrollments.total;
    const syncedItems = stats.users.synced + stats.categories.synced + stats.courses.synced + stats.enrollments.synced;
    return totalItems > 0 ? Math.round((syncedItems / totalItems) * 100) : 0;
  };

  const getStatusVariant = (percentage) => {
    if (percentage === 100) return 'success';
    if (percentage >= 75) return 'info';
    if (percentage >= 50) return 'warning';
    return 'danger';
  };

  const getPriorityTasks = () => {
    const tasks = [];
    if (stats.users.percentage < 100) {
      tasks.push({
        title: 'Migrar Usuarios',
        description: `${stats.users.total - stats.users.synced} usuarios pendientes`,
        route: '/users',
        priority: 'high',
        icon: 'bi-people'
      });
    }
    if (stats.categories.percentage < 100) {
      tasks.push({
        title: 'Migrar Categorías',
        description: `${stats.categories.total - stats.categories.synced} categorías pendientes`,
        route: '/categories',
        priority: 'medium',
        icon: 'bi-diagram-3'
      });
    }
    if (stats.courses.percentage < 100) {
      tasks.push({
        title: 'Migrar Cursos',
        description: `${stats.courses.total - stats.courses.synced} cursos pendientes`,
        route: '/courses',
        priority: 'medium',
        icon: 'bi-book'
      });
    }
    if (stats.enrollments.percentage < 100) {
      tasks.push({
        title: 'Procesar Matriculaciones',
        description: `${stats.enrollments.total - stats.enrollments.synced} matriculaciones pendientes`,
        route: '/enrollments',
        priority: 'low',
        icon: 'bi-person-plus'
      });
    }
    return tasks;
  };

  const StatCard = ({ title, icon, stats, color, route, details }) => (
    <Card className="h-100 border-0 shadow-sm hover-card" style={{cursor: 'pointer'}} onClick={() => navigate(route)}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h6 className="text-muted mb-1">{title}</h6>
            <div className="d-flex align-items-center">
              <span className="h4 mb-0 me-2">{stats.synced}</span>
              <small className="text-muted">/ {stats.total}</small>
            </div>
          </div>
          <div className={`bg-${color} text-white rounded-circle d-flex align-items-center justify-content-center`} style={{width: '60px', height: '60px'}}>
            <i className={`bi ${icon} fs-5`}></i>
          </div>
        </div>
        
        <div className="mb-3">
          <div className="d-flex justify-content-between mb-1">
            <small className="text-muted">Progreso</small>
            <small className={`fw-bold text-${getStatusVariant(stats.percentage)}`}>
              {stats.percentage}%
            </small>
          </div>
          <ProgressBar 
            now={stats.percentage} 
            variant={getStatusVariant(stats.percentage)} 
            style={{height: '6px'}}
          />
        </div>

        {details && (
          <div className="d-flex justify-content-between text-center">
            {title === 'Usuarios' && (
              <>
                <div>
                  <div className="small text-info fw-bold">{details.teachers.synced}</div>
                  <div className="small text-muted">Profesores</div>
                </div>
                <div>
                  <div className="small text-success fw-bold">{details.students.synced}</div>
                  <div className="small text-muted">Estudiantes</div>
                </div>
              </>
            )}
            {title === 'Categorías' && Object.entries(details).map(([key, value]) => (
              <div key={key}>
                <div className="small text-primary fw-bold">{value.synced}</div>
                <div className="small text-muted">{key}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-3">
          <Badge bg={stats.percentage === 100 ? 'success' : 'warning'} className="w-100">
            {stats.percentage === 100 ? 'Completado' : `${stats.total - stats.synced} pendientes`}
          </Badge>
        </div>
      </Card.Body>
    </Card>
  );

  const overallProgress = getOverallProgress();
  const priorityTasks = getPriorityTasks();

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Panel de Control</h2>
          <p className="text-muted mb-0">
            Sistema de Migración LMS - Última actualización: {lastUpdate.toLocaleTimeString()}
          </p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Button variant="outline-primary" size="sm" onClick={loadStats}>
            <i className="bi bi-arrow-clockwise me-1"></i>
            Actualizar
          </Button>
          <Badge bg={systemStatus === 'active' ? 'success' : 'danger'} className="px-3 py-2" title={systemError}>
            <i className={`bi ${systemStatus === 'active' ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-1`}></i>
            {systemStatus === 'active' ? 'Sistema Activo' : 'Error de Conexión'}
          </Badge>
        </div>
      </div>

      {/* Overall Progress */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Progreso General de Migración</h5>
            <Badge bg={getStatusVariant(overallProgress)} className="px-3 py-2">
              {overallProgress}% Completado
            </Badge>
          </div>
          <ProgressBar 
            now={overallProgress} 
            variant={getStatusVariant(overallProgress)}
            style={{height: '12px'}}
            className="mb-2"
          />
          <div className="d-flex justify-content-between text-muted small">
            <span>
              {stats.users.synced + stats.categories.synced + stats.courses.synced + stats.enrollments.synced} elementos migrados
            </span>
            <span>
              {stats.users.total + stats.categories.total + stats.courses.total + stats.enrollments.total} elementos totales
            </span>
          </div>
        </Card.Body>
      </Card>

      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        <Col md={6} lg={3}>
          <StatCard
            title="Usuarios"
            icon="bi-people"
            stats={stats.users}
            color="primary"
            route="/users"
            details={stats.users.details}
          />
        </Col>
        <Col md={6} lg={3}>
          <StatCard
            title="Categorías"
            icon="bi-diagram-3"
            stats={stats.categories}
            color="info"
            route="/categories"
            details={stats.categories.details}
          />
        </Col>
        <Col md={6} lg={3}>
          <StatCard
            title="Cursos"
            icon="bi-book"
            stats={stats.courses}
            color="success"
            route="/courses"
          />
        </Col>
        <Col md={6} lg={3}>
          <StatCard
            title="Matriculaciones"
            icon="bi-person-plus"
            stats={stats.enrollments}
            color="warning"
            route="/enrollments"
          />
        </Col>
      </Row>

      {/* Priority Tasks */}
      {priorityTasks.length > 0 && (
        <Row>
          <Col lg={8}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0">
                  <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                  Tareas Pendientes ({priorityTasks.length})
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                {priorityTasks.map((task, index) => (
                  <div 
                    key={index}
                    className="d-flex align-items-center p-3 border-bottom hover-bg-light"
                    style={{cursor: 'pointer'}}
                    onClick={() => navigate(task.route)}
                  >
                    <div className={`bg-${task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'warning' : 'info'} text-white rounded-circle d-flex align-items-center justify-content-center me-3`} style={{width: '40px', height: '40px'}}>
                      <i className={`bi ${task.icon}`}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-medium">{task.title}</div>
                      <small className="text-muted">{task.description}</small>
                    </div>
                    <Badge bg={task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'warning' : 'info'}>
                      {task.priority === 'high' ? 'Alta' : task.priority === 'medium' ? 'Media' : 'Baja'}
                    </Badge>
                    <i className="bi bi-chevron-right text-muted ms-2"></i>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
          
          <Col lg={4}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0">
                  <i className="bi bi-info-circle text-info me-2"></i>
                  Resumen del Sistema
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Estado del Sistema:</span>
                  <Badge bg={systemStatus === 'active' ? 'success' : 'danger'}>
                    {systemStatus === 'active' ? 'Activo' : 'Error'}
                  </Badge>
                </div>
                {systemError && (
                  <div className="mb-2">
                    <small className="text-danger">{systemError}</small>
                  </div>
                )}
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Progreso Total:</span>
                  <span className="fw-bold">{overallProgress}%</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Tareas Pendientes:</span>
                  <span className="fw-bold">{priorityTasks.length}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Última Actualización:</span>
                  <span className="small">{lastUpdate.toLocaleTimeString()}</span>
                </div>
              </Card.Body>
            </Card>

            {overallProgress === 100 && (
              <Alert variant="success" className="mt-3">
                <i className="bi bi-check-circle me-2"></i>
                <strong>¡Migración Completada!</strong><br/>
                Todos los elementos han sido migrados exitosamente.
              </Alert>
            )}
          </Col>
        </Row>
      )}

      {priorityTasks.length === 0 && (
        <Alert variant="success" className="text-center py-4">
          <i className="bi bi-check-circle-fill fs-1 text-success mb-3"></i>
          <h4>¡Migración Completada!</h4>
          <p className="mb-0">Todos los elementos han sido migrados exitosamente a Moodle.</p>
        </Alert>
      )}

      <style jsx>{`
        .hover-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
          transition: all 0.3s ease;
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;